
import {
  entitySlugForLocale,
  buildHreflangLinks,
  computeAlternateUrls,
} from '@/utils/hreflang'
import {
  hasListFilters,
  paginatedCanonical,
  paginatedTitle,
  paginationPage,
  withPageQuery,
  withPageSuffix,
} from '@/utils/paginationSeo'
import { buildOgLocaleMeta } from '@/utils/seo'
import BreadcrumbStructuredData from '@/components/BreadcrumbStructuredData'
import FaqList from '@/components/FaqList.vue'
import Breadcrumb from '@/components/Breadcrumb'
import CategoryListItem from '@/components/CategoryListItem'
import ProductList from '@/components/ProductList'
import BreadcrumbMixin from '@/mixins/breadcrumb'
import Loading from '@/components/Loading.vue'

export default {
  name: 'DomainIndex',
  components: {
    Breadcrumb,
    CategoryListItem,
    Loading,
    BreadcrumbStructuredData,
    ProductList,
    FaqList,
  },
  mixins: [BreadcrumbMixin],
  data() {
    return {
      breadcrumb_list: [],
      subcategory: null,
      category: null,
      domain: null,
      page: 1,
      languageMap: {},
    }
  },
  async fetch() {
    if (this.$route.params.subcategory) {
      await Promise.all([
        this.$getLanguageMap().then(map => {
          this.languageMap = map
        }),
        this.$categoryService
          .fetchCategoryPage(this.$route.params.subcategory)
          .then(({ data }) => {
            this.subcategory = data
            this.category = data.nested_parents_light
            this.domain = data.nested_parents_light.nested_parents_light
            this.$store.commit(
              'SET_RESTRICTED_LOCALES',
              data.restricted_locales || []
            )

            if (
              this.$route.params.domain !== this.domain.slug ||
              this.$route.params.category !== this.category.slug ||
              this.$route.params.subcategory !== this.subcategory.slug
            ) {
              const correctPath = this.localePath({
                name: 'categories-domain-category-subcategory',
                params: {
                  domain: this.domain.slug,
                  category: this.category.slug,
                  subcategory: this.subcategory.slug,
                },
              })
              if (process.server) {
                this.$nuxt.context.redirect(301, correctPath)
              } else {
                this.$router.replace(correctPath)
              }
              return
            }

            this.breadcrumb_list = [
              {
                text: this.$t(
                  'pages.categories.domain.category.subcategory.index.home'
                ),
                to: this.localePath({ name: 'index' }),
              },
              {
                text: this.domain.name,
                to: this.localePath({
                  name: 'categories-domain',
                  params: {
                    domain: this.$route.params.domain,
                  },
                }),
              },
              {
                text: this.category.name,
                to: this.localePath({
                  name: 'categories-domain-category',
                  params: {
                    domain: this.$route.params.domain,
                    category: this.$route.params.category,
                  },
                }),
              },
              {
                text: this.subcategory.name,
                to: this.localePath({
                  name: 'categories-domain-category-subcategory',
                  params: {
                    domain: this.$route.params.domain,
                    category: this.$route.params.category,
                    subcategory: this.$route.params.subcategory,
                  },
                }),
              },
            ]
          })
          .catch(error => {
            if (error?.response?.status === 451) {
              this.$451Error('category')
            } else {
              this.$404Error()
            }
          }),
      ])
    }

    if (
      this.domain &&
      this.category &&
      this.subcategory &&
      this.$route.params.domain === this.domain.slug &&
      this.$route.params.category === this.category.slug &&
      this.$route.params.subcategory === this.subcategory.slug
    ) {
      computeAlternateUrls({
        locales: this.$i18n.locales,
        localePath: this.localePath,
        routeName: 'categories-domain-category-subcategory',
        buildParams: code => {
          if ((this.$store.state.restricted_locales || []).includes(code))
            return null
          const domain = entitySlugForLocale(this, this.domain, code)
          const category = entitySlugForLocale(this, this.category, code)
          const subcategory = entitySlugForLocale(this, this.subcategory, code)
          return domain && category && subcategory
            ? { domain, category, subcategory }
            : null
        },
        store: this.$store,
      })
    }
  },
  head() {
    if (this.subcategory && this.domain && this.category) {
      const canonicalUrl = paginatedCanonical(
        this.$config.url +
          this.localePath({
            name: 'categories-domain-category-subcategory',
            params: {
              domain: this.domain.slug,
              category: this.category.slug,
              subcategory: this.subcategory.slug,
            },
          }),
        this.$route.query
      )
      const hreflangLinks = buildHreflangLinks({
        locales: this.$i18n.locales,
        configUrl: this.$config.url,
        localePath: this.localePath,
        routeName: 'categories-domain-category-subcategory',
        buildParams: code => {
          if ((this.$store.state.restricted_locales || []).includes(code))
            return null
          const domain = entitySlugForLocale(this, this.domain, code)
          const category = entitySlugForLocale(this, this.category, code)
          const subcategory = entitySlugForLocale(this, this.subcategory, code)
          return domain && category && subcategory
            ? { domain, category, subcategory }
            : null
        },
        store: this.$store,
      })
      return {
        title: paginatedTitle(
          this.subcategory.meta_title
            ? this.subcategory.meta_title
            : this.subcategory.name,
          this.$route.query,
          this.$t.bind(this)
        ),
        link: [
          {
            hid: 'i18n-can',
            rel: 'canonical',
            href: canonicalUrl,
          },
          ...this.lcpProductImagePreloadLinks,
          ...withPageQuery(hreflangLinks, this.$route.query),
        ],
        meta: [
          ...(this.hasListFilters
            ? [
                {
                  hid: 'robots',
                  name: 'robots',
                  content: 'noindex, follow',
                },
              ]
            : []),
          {
            hid: 'description',
            name: 'description',
            content: withPageSuffix(
              this.subcategory.meta_description
                ? this.subcategory.meta_description
                : this.stripHtml(this.subcategory.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          { hid: 'og:type', property: 'og:type', content: 'website' },
          {
            hid: 'og:url',
            property: 'og:url',
            content: canonicalUrl,
          },
          {
            hid: 'og:title',
            property: 'og:title',
            content: this.subcategory.meta_title
              ? this.subcategory.meta_title
              : this.subcategory.name,
          },
          {
            hid: 'og:description',
            property: 'og:description',
            content: withPageSuffix(
              this.subcategory.meta_description
                ? this.subcategory.meta_description
                : this.stripHtml(this.subcategory.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          {
            hid: 'og:site_name',
            property: 'og:site_name',
            content: 'AGM Vision',
          },
          ...buildOgLocaleMeta(this.$i18n.locale),
          {
            hid: 'og:image',
            property: 'og:image',
            content:
              this.subcategory.images && this.subcategory.images?.length > 0
                ? this.subcategory.images[0].url.startsWith('http')
                  ? this.subcategory.images[0].url.replace(/ /g, '%20')
                  : `${
                      this.$config.url
                    }${this.subcategory.images[0].url.replace(/ /g, '%20')}`
                : '',
          },
          {
            hid: 'og:image:alt',
            property: 'og:image:alt',
            content: this.subcategory.title_top || this.subcategory.name,
          },
          {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: this.subcategory.meta_title || this.subcategory.name,
          },
          {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: withPageSuffix(
              this.subcategory.meta_description
                ? this.subcategory.meta_description
                : this.stripHtml(this.subcategory.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          {
            hid: 'twitter:image',
            name: 'twitter:image',
            content:
              this.subcategory.images && this.subcategory.images?.length > 0
                ? this.subcategory.images[0].url.startsWith('http')
                  ? this.subcategory.images[0].url.replace(/ /g, '%20')
                  : `${
                      this.$config.url
                    }${this.subcategory.images[0].url.replace(/ /g, '%20')}`
                : '',
          },
        ],
      }
    }
  },
  computed: {
    urlToAdminPage() {
      return `${this.$config.adminUrl}/categories/${this.subcategory.id}`
    },
    lcpProductImagePreloadLinks() {
      if (!process.server) {
        return []
      }
      const product = this.$store.getters.products_list?.[0]
      const image = product?.images?.length
        ? product.images[0]
        : product?.variant?.images?.[0]
      if (!image?.url) {
        return []
      }
      const extension = image.extension || 'jpg'
      const dimension = this.$device.isMobile ? 160 : 280
      const webp = Boolean(
        this.$webpCropUrl(image, dimension, dimension, extension, 80)
      )
      const crop = webp ? this.$webpCropUrl : this.$cropUrl
      const src = crop(image, dimension, dimension, extension, 80)
      const highResSrc = crop(
        image,
        dimension * 2,
        dimension * 2,
        extension,
        80
      )
      return [
        {
          hid: 'lcp-product-image-preload',
          rel: 'preload',
          as: 'image',
          ...(webp ? { type: 'image/webp' } : {}),
          href: src,
          imagesrcset: `${highResSrc} 2x, ${src} 1x`,
          fetchpriority: 'high',
        },
      ]
    },
    siblingCategories() {
      if (!this.category?.children) return []
      return this.category.children.filter(c => c.id !== this.subcategory.id)
    },
    hasListFilters() {
      return hasListFilters(this.$route.query)
    },
    isPaginated() {
      return paginationPage(this.$route.query) !== null
    },
  },
  methods: {
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '').trim()
    },
  },
}
